import { Bucket } from '../../services/executionStatsService';

/**
 * Stage rows from `/agent/flow/context`.
 *
 * Run-level values (run_id, snapshot_key, …) and values equal to the stage
 * row's (stage, node_execution_id, activity_code) are not repeated on each
 * trace item, so an item carries them only where it differs from its row.
 *
 * Population stages list their traces. Customer stages (one run per customer)
 * are rolled up instead: their traces are empty, `customer_rollup` holds the
 * observations and decisions grouped by type, and `outcome` counts executions.
 * See agent-orchestration docs/latest-context-customer-mode-api-ui.md.
 */

export interface Technique {
    technique_id: string;
    name: string;
    reason?: string;
    rejection_reason?: string;
}

/** Sent only when false: the item has been superseded by a later run. */
type Superseded = { is_current?: false };

export interface FullDecision extends Superseded {
    decision_id: string;
    decision_type: string;
    rationale_text: string;
    chosen_option?: string;
    confidence_score?: number;
    selected_technique?: Technique;
    rejected_techniques?: Technique[];
    node_execution_id?: string;
    subject?: string;
    /** This decision set the node's result. */
    outcome_determining?: boolean;
    /** Options it turned down, each with its reason. */
    rejected_options?: Record<string, string>;
    /** Policies the decision cites. */
    policy_refs?: string[];
    /** Kept on roll-up samples, so a sample can be traced to its customer. */
    customer_id?: string;
}

/** A decision already listed in full under an earlier trace of the same stage. */
export interface DecisionReference {
    decision_id: string;
    decision_type: string;
    subject?: string;
    chosen_option?: string;
    listed_above: true;
}

export type Decision = FullDecision | DecisionReference;

export interface Observation extends Superseded {
    observation_type: string;
    description: string;
    severity?: string;
    node_execution_id?: string;
    /** Kept on flow-mode roll-up samples. */
    customer_id?: string;
}

/** Decisions of one type beyond the first few listed under an observation. */
export interface DecisionTypeRollup {
    decision_type: string;
    count: number;
    shown?: number;
    by_option?: Record<string, number>;
}

export interface ExplainabilityTrace {
    /** Null for the stage's decisions that cite no observation, grouped last. */
    observation: Observation | null;
    decisions: Decision[];
    decision_rollups?: DecisionTypeRollup[];
}

export interface StageOutcome {
    status?: string;
    nl_description?: string;
    duration_secs?: number;
    result_shape?: Record<string, { type: string; keys?: number; length?: number }>;
    /** When status is error. */
    error?: string;
    /** Headline numbers the node declares. */
    outcome_metrics?: Record<string, number>;
    // Rolled-up customer stage: summarised across its executions.
    executions?: number;
    by_status?: Record<string, number>;
    metrics?: Record<string, { sum: number; min: number; max: number }>;
    /** The first few failures. */
    errors?: { customer_id?: string; node_execution_id?: string; error: string }[];
}

export interface TechniqueApplied {
    technique_name: string;
    category?: string;
    input_row_count?: number;
    input_feature_count?: number;
    /** On a rolled-up customer stage: how many runs applied it. */
    executions?: number;
}

export interface RollupObservation {
    observation_type: string;
    count: number;
    by_severity?: Record<string, number>;
    sample_descriptions?: string[];
}

export interface RollupDecision {
    decision_type: string;
    count: number;
    by_option?: Record<string, number>;
    sample?: FullDecision[];
}

// What governed a stage: the ontology entries its configuration evaluated and
// the policies and documents behind them (backend _read_governance).

export interface GovernanceConcept {
    concept_code: string;
    preferred_label?: string;
    definition?: string;
    concept_type?: string;
    /** Config paths that evaluate it. */
    paths?: string[];
    /** `policy` when a policy parameter set the value, else `configuration`. */
    value_source?: string;
}

export interface GovernanceParameter {
    parameter_code: string;
    value?: unknown;
    applied_value?: unknown;
    unit?: string;
    operator?: string;
    basis?: string;
    scope_level?: string;
    concept_code?: string;
    config_keys?: string[];
    /** The policies that set it. */
    policy_codes?: string[];
}

export interface GovernancePolicy {
    policy_code: string;
    title?: string;
    section?: string;
    regulator?: string;
    jurisdiction?: string;
    scope_level?: string;
}

export interface GovernanceSource {
    source_doc_code?: string;
    title?: string;
    issuer?: string;
    citation?: string;
    pinpoint?: string;
    source_link?: string;
    provisional?: boolean;
    cited_by?: { kind: string; code: string; pinpoint?: string }[];
}

export interface Governance {
    concepts?: GovernanceConcept[];
    parameters?: GovernanceParameter[];
    policies?: GovernancePolicy[];
    sources?: GovernanceSource[];
    /** Codes the run recorded that the ontology projection doesn't hold. */
    unresolved?: Record<string, string[]>;
}

export interface CustomerRollup {
    executions: number;
    customers_processed: number;
    decisions: RollupDecision[];
    observations: RollupObservation[];
}

export interface ExplainabilityDataElement {
    stage: string;
    stage_category?: string;
    scope?: string;
    activity_code?: string;
    node_execution_id?: string;
    outcome?: StageOutcome;
    techniques_applied?: TechniqueApplied[];
    explainability_traces: ExplainabilityTrace[];
    customer_rollup?: CustomerRollup;
    governance?: Governance;
    trace_counts?: { observations: number; decisions: number };
}

export const isDecisionReference = (decision: Decision): decision is DecisionReference =>
    (decision as DecisionReference).listed_above === true;

/** The techniques a decision weighed: the one it chose, then the ones it rejected. */
export const techniqueOptions = (decision: FullDecision): Technique[] => {
    const seen = new Set<string>();
    return [decision.selected_technique, ...(decision.rejected_techniques ?? [])].filter((tech): tech is Technique => {
        if (!tech || seen.has(tech.technique_id)) return false;
        seen.add(tech.technique_id);
        return true;
    });
};

/** A stage outcome's status as a run bucket, so it reads like Run health's; null when unrecognised. */
export const outcomeBucket = (status: string | undefined): Bucket | null => {
    switch ((status || '').toLowerCase()) {
        case 'success':
        case 'succeeded':
            return 'success';
        case 'failure':
        case 'failed':
        case 'error':
            return 'failed';
        default:
            return null;
    }
};

export interface MergedTechnique extends TechniqueApplied {
    /** How many times the stage applied it with the same inputs. */
    times: number;
}

/** Techniques applied, with identical repeats (same name and inputs) folded into one. */
export const mergeTechniques = (techniques: TechniqueApplied[] = []): MergedTechnique[] => {
    const merged = new Map<string, MergedTechnique>();
    techniques.forEach((tech) => {
        const key = [tech.technique_name, tech.category, tech.input_row_count, tech.input_feature_count].join('|');
        const seen = merged.get(key);
        if (seen) seen.times += 1;
        else merged.set(key, { ...tech, times: 1 });
    });
    return Array.from(merged.values());
};

/** `P3 ×8, P4 ×5, +2 more` — the options a rolled-up decision took, most common first. */
export const optionSummary = (byOption: Record<string, number> = {}, shown = 3): string | null => {
    const entries = Object.entries(byOption).sort(([, a], [, b]) => b - a);
    if (entries.length === 0) return null;
    const listed = entries.slice(0, shown).map(([option, n]) => `${option} ×${n}`);
    if (entries.length > shown) listed.push(`+${entries.length - shown} more`);
    return listed.join(', ');
};

/** Each decision a stage made, once, with what it chose — for the stage's summary card. */
export const decisionChoices = (stage: ExplainabilityDataElement): { id: string; type: string; choice: string | null }[] => {
    if (stage.customer_rollup) {
        return stage.customer_rollup.decisions.map((rollup) => ({
            id: rollup.decision_type,
            type: rollup.decision_type,
            choice: optionSummary(rollup.by_option),
        }));
    }
    const seen = new Set<string>();
    return stage.explainability_traces
        .flatMap((trace) => trace.decisions)
        .filter((decision): decision is FullDecision => {
            if (isDecisionReference(decision) || seen.has(decision.decision_id)) return false;
            seen.add(decision.decision_id);
            return true;
        })
        .map((decision) => ({
            id: decision.decision_id,
            type: decision.decision_type,
            choice: decision.selected_technique?.name ?? decision.chosen_option ?? null,
        }));
};

/** Outputs the stage produced with nothing in them, which a successful status alone hides. */
export const emptyOutputs = (outcome: StageOutcome | undefined): string[] =>
    Object.entries(outcome?.result_shape ?? {})
        .filter(([, shape]) => shape.keys === 0 || shape.length === 0)
        .map(([name]) => name);

/**
 * How a stage ended, for its status dot and label. A rolled-up customer stage
 * counts its executions, so a few failures among many runs still read as failed.
 */
export const stageStatus = (outcome: StageOutcome | undefined): { bucket: Bucket | null; label: string; raw?: string } | null => {
    if (outcome?.status) {
        return { bucket: outcomeBucket(outcome.status), label: outcome.status, raw: outcome.status };
    }
    const byStatus = outcome?.by_status;
    if (!byStatus || Object.keys(byStatus).length === 0) return null;
    const total = Object.values(byStatus).reduce((a, b) => a + b, 0);
    const failed = Object.entries(byStatus)
        .filter(([status]) => outcomeBucket(status) === 'failed')
        .reduce((sum, [, n]) => sum + n, 0);
    const succeeded = Object.entries(byStatus)
        .filter(([status]) => outcomeBucket(status) === 'success')
        .reduce((sum, [, n]) => sum + n, 0);
    if (failed > 0) return { bucket: 'failed', label: `${failed} of ${total} failed` };
    if (succeeded === total) return { bucket: 'success', label: `${total} of ${total} succeeded` };
    return { bucket: null, label: Object.entries(byStatus).map(([status, n]) => `${n} ${status}`).join(', ') };
};

/** Most severe first. `severity_raw` values the API normalises (warn, high) never reach here. */
const SEVERITY_RANK: Record<string, number> = { error: 4, warning: 3, signal: 2, info: 1 };

export const severityRank = (severity: string | undefined): number => SEVERITY_RANK[(severity || '').toLowerCase()] ?? 0;

/** A rolled-up observation's worst severity, which orders the list. */
export const worstSeverity = (bySeverity: Record<string, number> = {}): string | null =>
    Object.keys(bySeverity).sort((a, b) => severityRank(b) - severityRank(a))[0] ?? null;

/** Observation counts by severity across a rolled-up stage, most severe first. */
export const severityTally = (observations: RollupObservation[] = []): [string, number][] => {
    const tally: Record<string, number> = {};
    observations.forEach((obs) => Object.entries(obs.by_severity ?? {}).forEach(([severity, n]) => {
        tally[severity] = (tally[severity] ?? 0) + n;
    }));
    return Object.entries(tally).sort(([a], [b]) => severityRank(b) - severityRank(a));
};

/**
 * A producer's `metric: value | metric: value | FLAG` description, split so the
 * flags can stand out. A description without that form comes back as one metric.
 */
export const describeSegments = (description: string): { metrics: string[]; flags: string[] } => {
    const parts = description.split(' | ').map((part) => part.trim()).filter(Boolean);
    if (parts.length < 2) return { metrics: [description], flags: [] };
    const isFlag = (part: string) => !part.includes(': ') && /[A-Z]/.test(part) && part === part.toUpperCase();
    const isLeadingFlag = (part: string) => /^[A-Z][A-Z0-9 _-]+[A-Z]: /.test(part); // "ABNORMAL Z-SCORE DETECTED: +2.37"
    return {
        metrics: parts.filter((part) => !isFlag(part) && !isLeadingFlag(part)),
        flags: parts.filter((part) => isFlag(part) || isLeadingFlag(part)),
    };
};

/** `n_circular_flows` → `Circular flows`. */
export const metricLabel = (key: string): string => {
    const words = key.replace(/^n_/, '').replace(/_/g, ' ').trim();
    return words.charAt(0).toUpperCase() + words.slice(1);
};

/**
 * A decision's confidence as a percentage, or null when it wasn't recorded.
 * The writer defaults a missing score to 0.0, so 0 means "not recorded" too.
 */
export const confidenceLabel = (score: number | undefined): string | null => {
    if (score === undefined || score === null || !(score > 0)) return null;
    return `${Math.round(score <= 1 ? score * 100 : score)}%`;
};

/** What a decision turned down and why: rejected techniques, then rejected options. */
export const alternativesOf = (decision: FullDecision): { name: string; reason?: string }[] => [
    ...(decision.rejected_techniques ?? []).map((tech) => ({ name: tech.name, reason: tech.rejection_reason })),
    ...Object.entries(decision.rejected_options ?? {})
        // An option is often the same technique under its id; don't list it twice.
        .filter(([option]) => !(decision.rejected_techniques ?? []).some((tech) => tech.technique_id === option))
        .map(([option, reason]) => ({ name: option, reason: typeof reason === 'string' ? reason : undefined })),
];

export const hasGovernance = (governance: Governance | undefined): governance is Governance =>
    Boolean(governance && (
        governance.concepts?.length || governance.parameters?.length || governance.policies?.length
        || governance.sources?.length || Object.keys(governance.unresolved ?? {}).length
    ));

/**
 * The decisions that set a group's result (`outcome_determining`), each once,
 * with the stage that made it — what an Insights row's "Why this result" shows.
 */
export const resultDecisions = (stages: ExplainabilityDataElement[]): { stage: string; decision: FullDecision }[] => {
    const seen = new Set<string>();
    const found: { stage: string; decision: FullDecision }[] = [];
    stages.forEach((stage) => stage.explainability_traces.forEach((trace) => trace.decisions.forEach((decision) => {
        if (isDecisionReference(decision) || !decision.outcome_determining || seen.has(decision.decision_id)) return;
        seen.add(decision.decision_id);
        found.push({ stage: stage.stage, decision });
    })));
    return found;
};
